import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { notifyOk, notifyWarn, notifyError } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/forms/fields";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const FALTANTE_LABEL = {
  nombre: "Nombre de la organización",
  razon_social: "Razón social",
  rfc: "RFC",
  firmante_principal: "Firmante principal",
  firmante_principal_contacto: "Correo o teléfono del firmante principal",
  sitio_activo: "Al menos un sitio activo",
};

export default function ExpedienteTab({ oid, perfil, faltantes, clientes, onReload, onIrATab }) {
  const [f, setF] = useState({
    razon_social: perfil?.razon_social || "",
    rfc: perfil?.rfc || "",
    tipo_persona: perfil?.tipo_persona || "",
    regimen_fiscal: perfil?.regimen_fiscal || "",
    email_facturacion: perfil?.email_facturacion || "",
    domicilio_fiscal: perfil?.domicilio_fiscal || {},
  });
  const [pagador, setPagador] = useState(perfil?.cliente_id_pagador || "");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setDom = (k, v) => setF((p) => ({ ...p, domicilio_fiscal: { ...(p.domicilio_fiscal || {}), [k]: v } }));

  const guardar = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/organizations/${oid}/perfil`, f);
      if (r.data.revalidacion_requerida) notifyWarn("Cambio material: el expediente volvió a revisión.");
      else notifyOk("Expediente guardado.");
      onReload();
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const guardarPagador = async () => {
    try {
      await api.put(`/organizations/${oid}/pagador`, { cliente_id_pagador: pagador || null });
      notifyOk("Pagador actualizado (dato comercial, no afecta accesos).");
      onReload();
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="tab-expediente">
      {faltantes?.length > 0 && (
        <div data-testid="expediente-faltantes" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm font-medium text-amber-800">Faltantes para validar el expediente</p>
          <ul className="mt-1 text-xs text-amber-700 list-disc pl-5">
            {faltantes.map((k) => <li key={k}>{FALTANTE_LABEL[k] || k}</li>)}
          </ul>
          <p className="mt-2 text-[11px] text-amber-700/80">
            Nada de esto bloquea crear sitios, dar de alta residentes, recibir eventos ni atender casos.
          </p>
          {(faltantes.includes("firmante_principal") || faltantes.includes("firmante_principal_contacto")) && (
            <div className="mt-3">
              <p className="text-[11px] text-amber-800">
                El firmante principal se marca en Representación → abre la persona → «Firmante
                principal del contrato».
              </p>
              <Button variant="outline" size="sm" data-testid="ir-a-representacion"
                onClick={() => onIrATab && onIrATab("representacion")}
                className="mt-2 rounded-lg border-amber-300 text-amber-800 hover:bg-amber-100">
                Ir a Representación
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Razón social">
          <Input data-testid="perfil-razon-social" className="rounded-xl" value={f.razon_social} onChange={(e) => set("razon_social", e.target.value)} />
        </Field>
        <Field label="RFC">
          <Input data-testid="perfil-rfc" className="rounded-xl" value={f.rfc} onChange={(e) => set("rfc", e.target.value.toUpperCase())} />
        </Field>
        <Field label="Tipo de persona (opcional)">
          <Select value={f.tipo_persona || "sin_definir"} onValueChange={(v) => set("tipo_persona", v === "sin_definir" ? "" : v)}>
            <SelectTrigger className="rounded-xl" data-testid="perfil-tipo-persona"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sin_definir">Sin definir</SelectItem>
              <SelectItem value="moral">Persona moral</SelectItem>
              <SelectItem value="fisica">Persona física</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Régimen fiscal (opcional)">
          <Input className="rounded-xl" value={f.regimen_fiscal} onChange={(e) => set("regimen_fiscal", e.target.value)} />
        </Field>
        <Field label="Correo de facturación (opcional)">
          <Input data-testid="perfil-email-facturacion" className="rounded-xl" value={f.email_facturacion} onChange={(e) => set("email_facturacion", e.target.value)} />
        </Field>
      </div>

      <div className="rounded-xl border border-gray-100 p-4 space-y-3">
        <p className="text-sm font-medium text-[#012472]">Domicilio fiscal (opcional)</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {[["calle", "Calle"], ["numero_exterior", "No. exterior"], ["numero_interior", "No. interior"],
            ["colonia", "Colonia"], ["cp", "C.P."], ["municipio_alcaldia", "Municipio / alcaldía"],
            ["estado", "Estado"], ["pais", "País"]].map(([k, label]) => (
            <Field key={k} label={label}>
              <Input data-testid={`perfil-dom-${k}`} className="rounded-xl" value={(f.domicilio_fiscal || {})[k] || ""} onChange={(e) => setDom(k, e.target.value)} />
            </Field>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 p-4 space-y-3">
        <p className="text-sm font-medium text-[#012472]">Pagador (dato comercial · opcional)</p>
        <p className="text-xs text-slate-500">
          Quién paga no define accesos ni scope. Cambiarlo no mueve el tenant ni invalida sesiones.
        </p>
        <div className="flex flex-wrap gap-2">
          <Select value={pagador || "ninguno"} onValueChange={(v) => setPagador(v === "ninguno" ? "" : v)}>
            <SelectTrigger className="rounded-xl w-full sm:w-64" data-testid="perfil-pagador-select"><SelectValue placeholder="Sin pagador" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ninguno">Sin pagador</SelectItem>
              {(clientes || []).map((c) => (<SelectItem key={c.id} value={c.id}>{c.nombre_completo || "(sin nombre)"}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button variant="outline" className="rounded-xl" onClick={guardarPagador} data-testid="perfil-pagador-guardar">Guardar pagador</Button>
        </div>
      </div>

      <div className="flex justify-end">
        <Button data-testid="perfil-guardar-button" disabled={saving}
          className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white" onClick={guardar}>
          Guardar borrador
        </Button>
      </div>
    </div>
  );
}
